import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, Grid, MenuItem, Paper, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import {
  addEntity,
  getEntityAdminUsers,
  deleteEntityAdminUser,
  getEntityByID,
  saveEntityAdminUser,
  updateEntityAdminUser,
  updateEntity
} from '../../../features/entity/entity.slice';
import { getAllRoles } from '../../../features/role/role.slice';
import { useTitle } from '../../../hooks/titleProvider';
import PrimaryButton from '../../ui/buttons/primaryButton';
import Input from '../../ui/forms/fields/input';
import Select from '../../ui/forms/fields/select';
import AdminUserManager from './adminUsers';

const validationSchema = yup.object().shape({
  name: yup.string(),
  registeredName: yup.string(),
  registrationNumber: yup.string(),
  contactPrimaryNumber: yup.string(),
  contactSecondaryNumber: yup.string(),
  contactEmailAddress: yup.string().email('Invalid email format'),
  entityType: yup.string(),
  entitySystemType: yup.string(),
  address1: yup.string(),
  address2: yup.string(),
  suburb: yup.string(),
  city: yup.string(),
  country: yup.string(),
  postalCode: yup.string()
});

const GeneralDetails = () => {
  const { setTitle } = useTitle();
  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const entityId = user?.entity;
  const entity = useSelector((state) => state.entity.entity);
  const loading = useSelector((state) => state.entity.loading);
  const adminUsers = useSelector((state) => state.entity.adminUsers);
  const roles = useSelector((state) => state.role.roles);
  const loadingAdminUsers = useSelector((state) => state.entity.fetchingAdminUsers);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAdminUser, setCurrentAdminUser] = useState({ username: '', email: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminUserToDelete, setAdminUserToDelete] = useState(null);

  useEffect(() => {
    setTitle('General Details');
    if (entityId) {
      dispatch(getEntityByID(entityId));
    }
    return () => {
      setTitle('');
    };
  }, [entityId, dispatch]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {}
  });

  useEffect(() => {
    if (entityId) {
      dispatch(getEntityAdminUsers(entityId));
    }
  }, [entityId, dispatch]);

  useEffect(() => {
    dispatch(getAllRoles());
  }, [dispatch]);

  const onSubmit = (data) => {
    const physicalAddress = {
      address1: data.address1,
      address2: data.address2,
      suburb: data.suburb,
      city: data.city,
      country: data.country,
      postalCode: data.postalCode
    };

    const entityData = {
      ...data,
      physicalAddress
    };

    if (entityId) {
      dispatch(updateEntity({ _id: entityId, ...entityData }));
    } else {
      dispatch(addEntity(entityData));
    }
  };

  useEffect(() => {
    if (entity) {
      reset({
        name: entity?.name || '',
        registeredName: entity?.registeredName || '',
        registrationNumber: entity?.registrationNumber || '',
        contactPrimaryNumber: entity?.contactPrimaryNumber || '',
        contactSecondaryNumber: entity?.contactSecondaryNumber || '',
        contactEmailAddress: entity?.contactEmailAddress || '',
        entityType: entity?.entityType || '',
        entitySystemType: entity?.entitySystemType || '',
        address1: entity?.physicalAddress?.address1 || '',
        address2: entity?.physicalAddress?.address2 || '',
        suburb: entity?.physicalAddress?.suburb || '',
        city: entity?.physicalAddress?.city || '',
        country: entity?.physicalAddress?.country || '',
        postalCode: entity?.physicalAddress?.postalCode || ''
      });
    }
  }, [entity, reset]);

  const handleOpenDialog = (adminUser = { username: '', email: '', roles: [] }) => {
    setCurrentAdminUser(adminUser);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentAdminUser({ username: '', email: '' });
  };

  const handleSaveAdminUser = (data) => {
    const newAdminUser = { ...data, entity: entityId };
    if (entityId) {
      if (currentAdminUser?._id) {
        dispatch(updateEntityAdminUser({ id: currentAdminUser._id, ...newAdminUser }))
          .unwrap()
          .then(() => {
            dispatch(getEntityAdminUsers(entityId));
          });
      } else {
        dispatch(saveEntityAdminUser(newAdminUser))
          .unwrap()
          .then(() => {
            dispatch(getEntityAdminUsers(entityId));
          });
      }
    }
    handleCloseDialog();
  };

  const handleDeleteAdminUser = (id) => {
    setAdminUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAdminUser = () => {
    dispatch(deleteEntityAdminUser(adminUserToDelete))
      .unwrap()
      .then(() => {
        dispatch(getEntityAdminUsers(entityId));
      });
    setDeleteDialogOpen(false);
    setAdminUserToDelete(null);
  };

  if (!entity) return <CircularProgress />;

  return (
    <Box>
      <Paper
        sx={{
          padding: theme.spacing(2),
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          boxShadow: 'none'
        }}>
        <form key={entity && entity._id} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                General Details
              </Typography>
              <Input
                label="Name"
                name="name"
                {...register('name')}
                control={control}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
              />
              <Input
                label="Registered Name"
                name="registeredName"
                {...register('registeredName')}
                control={control}
                error={!!errors.registeredName}
                helperText={errors.registeredName?.message}
                fullWidth
              />
              <Input
                label="Registration Number"
                name="registrationNumber"
                {...register('registrationNumber')}
                control={control}
                error={!!errors.registrationNumber}
                helperText={errors.registrationNumber?.message}
                fullWidth
              />
              <Input
                label="Primary Contact Number"
                name="contactPrimaryNumber"
                {...register('contactPrimaryNumber')}
                control={control}
                error={!!errors.contactPrimaryNumber}
                helperText={errors.contactPrimaryNumber?.message}
                fullWidth
              />
              <Input
                label="Secondary Contact Number"
                name="contactSecondaryNumber"
                {...register('contactSecondaryNumber')}
                control={control}
                fullWidth
              />
              <Input
                label="Contact Email Address"
                name="contactEmailAddress"
                {...register('contactEmailAddress')}
                control={control}
                error={!!errors.contactEmailAddress}
                helperText={errors.contactEmailAddress?.message}
                fullWidth
              />
              <Select
                label="Entity Type"
                control={control}
                name="entityType"
                error={!!errors.entityType}
                defaultValue=""
                fullWidth>
                <MenuItem value="Proprietary Limited Company (Pty Ltd)">
                  Proprietary Limited Company (Pty Ltd)
                </MenuItem>
              </Select>
              <Select
                label="System Type"
                control={control}
                name="entitySystemType"
                error={!!errors.entitySystemType}
                defaultValue=""
                fullWidth>
                <MenuItem value="Company/Organization">Company/Organization</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
              <Input
                label="Physical Address Line 1"
                name="address1"
                {...register('address1')}
                control={control}
                error={!!errors.address1}
                helperText={errors.address1?.message}
                fullWidth
              />
              <Input
                label="Physical Address Line 2"
                name="address2"
                {...register('address2')}
                control={control}
                fullWidth
              />
              <Input
                label="Suburb"
                name="suburb"
                {...register('suburb')}
                control={control}
                error={!!errors.suburb}
                helperText={errors.suburb?.message}
                fullWidth
              />
              <Input
                label="City"
                name="city"
                {...register('city')}
                control={control}
                error={!!errors.city}
                helperText={errors.city?.message}
                fullWidth
              />
              <Input
                label="Country"
                name="country"
                {...register('country')}
                control={control}
                error={!!errors.country}
                helperText={errors.country?.message}
                fullWidth
              />
              <Input
                label="Postal Code"
                name="postalCode"
                {...register('postalCode')}
                control={control}
                error={!!errors.postalCode}
                helperText={errors.postalCode?.message}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item xs={12} mt={3} display="flex" justifyContent="flex-end">
            <PrimaryButton type="submit" color="primary">
              {entityId ? 'Update' : 'Submit'}
            </PrimaryButton>
          </Grid>
        </form>
      </Paper>
      <Paper
        sx={{
          padding: theme.spacing(2),
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          boxShadow: 'none'
        }}>
        <Typography variant="h6" gutterBottom>
          Administrative Users
        </Typography>
        <AdminUserManager
          adminUsers={adminUsers}
          roles={roles}
          handleOpenDialog={handleOpenDialog}
          loadingAdminUsers={loadingAdminUsers}
          handleDeleteAdminUser={handleDeleteAdminUser}
          handleCloseDialog={handleCloseDialog}
          currentAdminUser={currentAdminUser}
          setAdminUserToDelete={setAdminUserToDelete}
          setDeleteDialogOpen={setDeleteDialogOpen}
          deleteDialogOpen={deleteDialogOpen}
          setCurrentAdminUser={setCurrentAdminUser}
          dialogOpen={dialogOpen}
          confirmDeleteAdminUser={confirmDeleteAdminUser}
          handleSaveAdminUser={handleSaveAdminUser}
        />
      </Paper>
    </Box>
  );
};

export default GeneralDetails;
